'use client';

import { useEffect, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Menu, type MenuItem } from '@/components/ui';
import { Avatar } from '@/components/ui/avatar';
import { useTheme, type ThemePref } from '@/components/ui/use-theme';
import { CircleHalf, GearSix, SignOut, UserCircle, UserSwitch } from '@/components/icons';
import { useDirectory } from '@/lib/directory-context';
import { resolveActor, roster } from '@/lib/directory';
import { deescalateAction, devSignInAs, loadMyElevation } from '@/app/actions';
import { signOut } from '@/lib/auth-client';
import { errorMessage } from '@/domain/types';
import { dateLocaleFor, type PlainKey } from '@/i18n';
import type { ActiveElevation } from '@/read';
import { reach } from '@/lib/reach';
import { useT } from '@/lib/locale-context';
import styles from './profile-menu.module.css';

/** What the head says under the name: the handle, or without one the sign-in
 *  address (docs/DESIGN_SYSTEM.md "The person and the instance", rule 1). */
export interface ProfileIdentity {
  handle: string | null;
  email: string;
}

/** The three appearance values, in the order they run from light to dark. */
const THEME_CHOICES: readonly { pref: ThemePref; label: PlainKey }[] = [
  { pref: 'light', label: 'shell.theme.light' },
  { pref: 'system', label: 'shell.theme.auto' },
  { pref: 'dark', label: 'shell.theme.dark' },
];

/**
 * The profile menu (docs/DESIGN_SYSTEM.md "The person and the instance",
 * rules 1 and 2, built 2026-10-18): a kit `Menu`, so the anchored popover on
 * desktop, the `Sheet` on compact, Escape and focus all come from the kit.
 *
 * Four things. Who I am: the head (avatar, name, the handle or the address in
 * mono). Where I go: Account, and Admin for an operator. One device setting:
 * Appearance, a value row wearing its choice. The way out: Sign out, past the
 * hairline. On dev one more value row, "Persona", whose values are the
 * seeded roster; picking one is a REAL sign-in (`devSignInAs`, hard-disabled
 * in production), never an impersonation cookie.
 *
 * A value row opens its values as their own menu one frame after this one
 * closes (the `Notify me…` pattern, notify-menu.tsx): on compact the first
 * sheet has to finish closing before the second can call `showModal`, and a
 * sheet inside a sheet takes no taps on iOS.
 *
 * Override mode (rule 2) is a state the person WEARS, so it sits in the head
 * as one line with its one act, End, and only while it is worn. It is set
 * nowhere in the UI until a gate reads it (the board item "Override mode is set
 * nowhere in the UI").
 */
export function ProfileMenu({
  viewer,
  identity,
  devMode,
  compact,
  operator = false,
}: {
  viewer: string;
  identity?: ProfileIdentity | null;
  devMode?: boolean;
  /** Trigger form: the avatar alone, without the name beside it. */
  compact?: boolean;
  /** The viewer runs this instance (persons.operator): show the Admin entry. */
  operator?: boolean;
}) {
  const t = useT();
  const router = useRouter();
  const directory = useDirectory();
  const { pref, setPref } = useTheme();
  const [open, setOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [personasOpen, setPersonasOpen] = useState(false);
  const [pending, start] = useTransition();
  const me = resolveActor(viewer, directory);
  const people = devMode ? roster() : [];
  const persona = people.find((p) => p.ref === viewer);

  function leave() {
    start(async () => {
      // Ends the better-auth session server-side (the cookie is cleared by the
      // response), then a full reload so nothing server-resolved survives.
      await signOut();
      window.location.assign('/signin');
    });
  }

  function signInAs(ref: string) {
    if (ref === viewer) return;
    start(async () => {
      // Dev only, no seat for a message: on a network failure nothing moves.
      const res = await reach(() => devSignInAs(ref));
      if (res && 'unreached' in res) return;
      // A real session now exists for the picked persona; a full reload so the
      // server-resolved viewer updates (the same pattern as the sign-in form).
      window.location.assign('/');
    });
  }

  const themeWord = THEME_CHOICES.find((c) => c.pref === pref)?.label ?? 'shell.theme.auto';

  const items: MenuItem[] = [
    { kind: 'separator' },
    {
      label: t('shell.page.account'),
      icon: <UserCircle aria-hidden />,
      testId: 'profile-account-link',
      onSelect: () => router.push('/account'),
    },
    // The operator plane (docs/MODEL.md §6b): shown only to an operator. Not a
    // nav-rail item, because it is not work.
    ...(operator
      ? [
          {
            label: t('shell.page.admin'),
            icon: <GearSix aria-hidden />,
            testId: 'profile-admin-link',
            onSelect: () => router.push('/admin'),
          } satisfies MenuItem,
        ]
      : []),
    { kind: 'separator' },
    {
      label: t('shell.profile.appearance'),
      icon: <CircleHalf aria-hidden />,
      value: t(themeWord),
      testId: 'profile-appearance',
      onSelect: () => requestAnimationFrame(() => setAppearanceOpen(true)),
    },
    ...(devMode
      ? [
          {
            label: t('shell.profile.signInAs'),
            icon: <UserSwitch aria-hidden />,
            value: persona?.name ?? '',
            testId: 'profile-sign-in-as',
            disabled: pending,
            onSelect: () => requestAnimationFrame(() => setPersonasOpen(true)),
          } satisfies MenuItem,
        ]
      : []),
    { kind: 'separator' },
    {
      label: t('shell.profile.signOut'),
      icon: <SignOut aria-hidden />,
      testId: 'sign-out',
      disabled: pending,
      onSelect: leave,
    },
  ];

  return (
    <div className={styles.wrap}>
      <Menu
        open={open}
        onOpenChange={setOpen}
        align="end"
        ariaLabel={t('shell.profile.sheet')}
        contentTestId="profile-menu"
        head={
          <>
            <div className={styles.identity}>
              <Avatar actor={me} size={34} title={false} />
              <div className={styles.idText}>
                <span className={styles.idName}>{me.name}</span>
                {identity && (
                  <span className={styles.idAddress} data-testid="profile-identity">
                    {identity.handle
                      ? t('shell.profile.handle', { handle: identity.handle })
                      : identity.email}
                  </span>
                )}
              </div>
            </div>
            {/* Mounted only while the menu is up, so its read does not run on
                every page (the compact Sheet keeps its content mounted). */}
            {open && <WornOverride />}
          </>
        }
        trigger={({ open: isOpen, toggle }) => (
          <button
            type="button"
            className={styles.trigger}
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={t('shell.profile.menu')}
            title={me.name}
            data-testid="profile-trigger"
          >
            <Avatar actor={me} size={28} title={false} />
            {!compact && <span className={styles.triggerName}>{me.name}</span>}
          </button>
        )}
        items={items}
      />
      {/* The values, each opened by its value row. */}
      <Menu
        detached
        open={appearanceOpen}
        onOpenChange={setAppearanceOpen}
        align="end"
        ariaLabel={t('shell.profile.appearance')}
        contentTestId="profile-appearance-menu"
        trigger={() => null}
        items={[
          { kind: 'label', label: t('shell.profile.appearance') },
          ...THEME_CHOICES.map(
            (c): MenuItem => ({
              label: t(c.label),
              selected: pref === c.pref,
              onSelect: () => setPref(c.pref),
            }),
          ),
        ]}
      />
      {devMode && (
        <Menu
          detached
          open={personasOpen}
          onOpenChange={setPersonasOpen}
          align="end"
          ariaLabel={t('shell.profile.signInAs')}
          contentTestId="profile-personas"
          trigger={() => null}
          items={[
            { kind: 'label', label: t('shell.profile.testing') },
            ...people.map(
              (p): MenuItem => ({
                label: p.name,
                icon: <Avatar actor={p} size={20} title={false} />,
                selected: p.ref === viewer,
                disabled: pending,
                onSelect: () => signInAs(p.ref),
              }),
            ),
          ]}
        />
      )}
    </div>
  );
}

/** The time of day, in the page's language (`dateLocaleFor`). Runs after mount
 *  only (the state loads in an effect), so `navigator` is there. */
function formatTime(d: Date | string, locale: string): string {
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Worn override mode (docs/DECISIONS.md "elevation"; DESIGN_SYSTEM.md "The
 * person and the instance", rule 2): one line under the head, "Override mode ·
 * until HH:MM", with its one act, End. Renders nothing while none is worn,
 * and nothing while it loads.
 */
function WornOverride() {
  const t = useT();
  const [state, setState] = useState<ActiveElevation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, start] = useTransition();

  useEffect(() => {
    let alive = true;
    void reach(() => loadMyElevation()).then((r) => {
      if (alive && (r === null || !('unreached' in r))) setState(r);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!state) return null;

  const end = () =>
    start(async () => {
      setError(null);
      const res = await reach(() => deescalateAction());
      if (res.ok || res.error.code === 'no_change') setState(null);
      else setError(t.msg(errorMessage(res.error)));
    });

  return (
    <div className={styles.worn} data-testid="profile-override">
      <div className={styles.wornLine}>
        <span className={styles.wornText}>
          {t('shell.elevate.until', {
            time: formatTime(state.expiresAt, dateLocaleFor(t.locale, navigator.languages)),
          })}
        </span>
        {/* A menu act (the arrow keys reach it), never the opening focus:
            the kit skips the head for that (menu.tsx `head`). */}
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={styles.wornEnd}
          disabled={pending}
          onClick={end}
        >
          {t('shell.elevate.end')}
        </button>
      </div>
      {error && <span className={styles.wornError}>{error}</span>}
    </div>
  );
}
